import type { ProductAd, ProductAdComponentType } from '@/scopes/product-ads/domain/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { useCMSPageField } from '@/cms/fields'
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url'
import { useIntl } from '@/intl'
import { useOnScreen } from '@/scopes/common/components/hooks/useOnScreen'
import { ProductAdsComponent } from '@/scopes/product-ads/components/ProductAds'
import { useProductAds, useProductAdsRendered, useUpdateProductAdViewed } from '@/scopes/product-ads/domain/hooks'
import styles from './ProductAdsBanner.module.css'

interface ProductAdsBannerProps {
  componentType: Extract<ProductAdComponentType, 'LandingBanner'>
  compact?: boolean
}

interface ProductAdsBannerContentProps {
  data: ProductAd
  compact?: boolean
}

function ProductAdsBannerContent({ data, compact }: ProductAdsBannerContentProps) {
  const intl = useIntl()
  const ref = useRef<HTMLDivElement>(null)
  const isOnScreen = useOnScreen(ref, { threshold: 0.5 })
  const [isViewed, setIsViewed] = useState(false)

  useProductAdsRendered(data.rendered)

  const { update } = useUpdateProductAdViewed(data.viewed)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> = setTimeout(() => {}, 0)
    if (isOnScreen && !isViewed) {
      timer = setTimeout(async () => {
        if (isOnScreen) {
          await update()
          setIsViewed(true)
        }
      }, 1000)
    }
    else {
      clearTimeout(timer)
    }

    return () => clearTimeout(timer)
  }, [isOnScreen, isViewed, update])

  const { image, logo, heading, subHeading, clicked } = data

  return (
    <div ref={ref} className={cn(styles.wrapper, compact && styles.compact)}>
      <img
        className={cn(styles.backgroundImage, compact && styles.compact)}
        src={buildImgSrc(image, 375)}
        srcSet={buildImgSrcSet(image, [375, 600, 720, 1440, 2000, 2400])}
        sizes="100vw"
        fetchPriority="high"
        alt={heading}
      />
      <img className={cn(styles.logo, compact && styles.compact)} src={logo} alt="Brand logo" />
      <div className={cn(styles.infoBox, compact && styles.compact)}>
        <div className={cn(styles.infoBoxContent, compact && styles.compact)}>
          <Typography variant="display-g-regular">
            {heading}
          </Typography>
          <span className={cn(styles.infoBoxBodyWrapper, compact && styles.compact)}>
            <Typography variant="body-m-regular-flat">{subHeading}</Typography>
          </span>
        </div>
        <Button
          className={cn(styles.actionButton, compact && styles.compact)}
          fullWidth
          variant="primary"
          as="a"
          href={clicked}
          target="_blank"
        >
          {intl.formatMessage({ id: 'search_result_product_ads_banner_action_text' })}
        </Button>
      </div>
    </div>
  )
}

export function ProductAdsBanner({ compact, componentType }: ProductAdsBannerProps) {
  const contextItemId = useCMSPageField('contextItemId')
  const { isLoading, isFetched, error, data } = useProductAds({ contextItemId, componentType })
  const intl = useIntl()
  const bannerData = data?.[0]

  const wrapperProps = {
    infoLabel: intl.formatMessage({ id: 'search_result_product_ads_label' }),
    infoContent: intl.formatMessage({ id: 'search_result_product_ads_content' }),
    className: cn(styles.component, compact && styles.compact),
  }

  if (isLoading && !isFetched) {
    return (
      <ProductAdsComponent {...wrapperProps}>
        <div className={cn(styles.wrapper, compact && styles.compact)}>
          <IndeterminateProgressCircle className={styles.loadingSpinner} />
        </div>
      </ProductAdsComponent>
    )
  }

  if (error || !bannerData)
    return null

  return (
    <ProductAdsComponent {...wrapperProps}>
      <ProductAdsBannerContent data={bannerData} compact={compact} />
    </ProductAdsComponent>
  )
}
